import axios from "axios";

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const FILENAME = "IGF-Registration-Regulations.pdf";

/** UUID only — strips @, full URLs, and localhost download paths. */
export function esignDocumentId(legalesignDocId) {
  const raw = String(legalesignDocId || "").replace(/^@+/, "").trim();
  if (!raw || raw.includes("..")) return "";
  const match = raw.match(UUID_RE);
  return match ? match[0] : "";
}

/**
 * Same-origin path. Do not prefix REACT_APP_API_URL (that is localhost:5000 in .env).
 * Local CRA proxies /api → :5000. Deployed hosts serve /api on the same origin.
 */
export function esignDownloadUrl(legalesignDocId) {
  const id = esignDocumentId(legalesignDocId);
  if (!id) return "#";
  return `/api/esign-documents/download/${id}`;
}

/** GET the PDF proxy as a Blob; throws with the API's error message on failure. */
async function fetchEsignPdfBlob(legalesignDocId, params) {
  const path = esignDownloadUrl(legalesignDocId);
  if (path === "#") throw new Error("Missing document id");

  let res;
  try {
    res = await axios.get(path, { responseType: "blob", params });
  } catch (err) {
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        throw new Error(parsed.error || parsed.message || "Download failed");
      } catch (inner) {
        if (!(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw new Error(err.response?.data?.error || err.message || "Download failed");
  }
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
  const type = String(res.headers["content-type"] || blob.type || "").toLowerCase();

  if (type.includes("json") || type.includes("html") || blob.size < 8) {
    let message = "Download failed";
    try {
      const parsed = JSON.parse(await blob.text());
      message = parsed.error || parsed.message || message;
    } catch (_) { /* not JSON */ }
    throw new Error(message);
  }

  return type.includes("pdf") ? blob : new Blob([blob], { type: "application/pdf" });
}

/** Fetch PDF as blob and save. Never navigates the tab to localhost. */
export async function downloadEsignPdf(legalesignDocId) {
  const file = await fetchEsignPdfBlob(legalesignDocId);
  const objectUrl = window.URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = FILENAME;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * Admin preview: open the PDF (signed or not) in a new tab.
 * Tab opened before the await so popup blockers allow it.
 */
export async function previewEsignPdf(legalesignDocId) {
  const tab = window.open("", "_blank");
  try {
    const file = await fetchEsignPdfBlob(legalesignDocId, { preview: 1 });
    const objectUrl = window.URL.createObjectURL(file);
    if (tab) tab.location.href = objectUrl;
    else window.open(objectUrl, "_blank");
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
}
